import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getPodcastEpisodesPaginated,
  PROF_BAS_BLOEM_WEEKLY_SHOW_ID,
} from '@domain/features/podcasts/podcasts-feature'
import type { PodcastEpisode } from '@domain/models/podcast'
import { getContentRefreshPolicy } from '@presentation/hooks/content-refresh-policy'
import { useAutoRefreshOnForeground } from '@presentation/hooks/useAutoRefreshOnForeground'
import { useSsrPageData } from '@presentation/ssr/useSsrPageData'
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore'

const PODCASTS_PAGE_SIZE = 12
type PodcastsFetchPolicy = 'default' | 'server'

export function usePodcasts() {
  const ssrPodcastsList = useSsrPageData()?.podcastsList
  const [episodes, setEpisodes] = useState<PodcastEpisode[]>(
    ssrPodcastsList?.episodes ?? [],
  )
  const [loading, setLoading] = useState(!ssrPodcastsList)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(
    null,
  )
  const [hasMore, setHasMore] = useState(ssrPodcastsList?.hasMore ?? true)
  const requestIdRef = useRef(0)
  const refreshPolicy = getContentRefreshPolicy('list')

  const loadInitial = useCallback(async (
    fetchPolicy: PodcastsFetchPolicy,
    showInitialLoading: boolean,
  ) => {
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId

    if (showInitialLoading) {
      setLoading(true)
    }

    try {
      setError(null)
      const result = await getPodcastEpisodesPaginated(
        PROF_BAS_BLOEM_WEEKLY_SHOW_ID,
        PODCASTS_PAGE_SIZE,
        null,
        fetchPolicy,
      )
      if (requestId === requestIdRef.current) {
        setEpisodes(result.episodes)
        setLastDoc(result.lastDoc)
        setHasMore(result.hasMore)
      }
    } catch (loadError) {
      if (requestId === requestIdRef.current) {
        setError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load podcast episodes.',
        )
      }
      throw loadError
    } finally {
      if (showInitialLoading && requestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }, [])

  const {
    triggerRefresh,
    lastSuccessAt,
    isRefreshing,
  } = useAutoRefreshOnForeground({
    refresh: async ({ reason }) => {
      const fetchPolicy: PodcastsFetchPolicy = reason === 'mount' ? 'default' : 'server'
      await loadInitial(fetchPolicy, reason === 'mount' && episodes.length === 0)
    },
    staleAfterMs: refreshPolicy.staleAfterMs,
    pollIntervalMs: refreshPolicy.pollIntervalMs,
    minRefreshGapMs: refreshPolicy.minRefreshGapMs,
    maxFailureBackoffMs: refreshPolicy.maxFailureBackoffMs,
  })

  useEffect(() => {
    void triggerRefresh('mount', { force: true })
  }, [triggerRefresh])

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return
    }

    try {
      setLoadingMore(true)
      setError(null)
      const result = await getPodcastEpisodesPaginated(
        PROF_BAS_BLOEM_WEEKLY_SHOW_ID,
        PODCASTS_PAGE_SIZE,
        lastDoc,
      )
      setEpisodes((previous) => [...previous, ...result.episodes])
      setLastDoc(result.lastDoc)
      setHasMore(result.hasMore)
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : 'Failed to load more podcast episodes.',
      )
    } finally {
      setLoadingMore(false)
    }
  }, [hasMore, lastDoc, loading, loadingMore])

  const refresh = useCallback(async () => {
    await triggerRefresh('manual', { force: true })
  }, [triggerRefresh])

  return {
    episodes,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refresh,
    lastUpdatedAt: lastSuccessAt,
    isRefreshing,
  }
}
